/**
 * Diagram generation (issue #1649) — the frontend trigger for the
 * generate-and-persist endpoint built in #1073/#1075/#1077.
 *
 * `POST /diagrams/generate` always persists (unlike a GraphQL preview), so
 * it lives here alongside the other REST write calls rather than in
 * `graphql/queries`. It returns 409 when a generated diagram of the same
 * type already exists for the entity, unless `overwriteExisting: true` is
 * passed — callers are expected to catch that `ApiError` and re-call with
 * the flag set after user confirmation (see `ServiceDiagramsPage`).
 */

import type { Diagram } from '../../graphql/types';
import { apiRequest } from './http';

/**
 * Only `Service` + `c4-system-context` generation exists today
 * (backend/src/entities/Diagram.ts `DiagramType.C4SystemContext`); this
 * input shape mirrors the backend route's validation in
 * `backend/src/routes/diagrams.ts`.
 */
export interface GenerateDiagramInput {
  entityType: 'Service';
  entityId: string;
  type: 'c4-system-context';
  includeDepth?: number;
  overwriteExisting?: boolean;
}

/**
 * POST /api/v1/diagrams/generate
 *
 * Generates (and persists) a System Context diagram for a Service. Replaces
 * an existing generated diagram's content only when `overwriteExisting` is
 * `true`; otherwise the backend rejects with 409 (`ApiError.status === 409`).
 */
export async function generateDiagram(input: GenerateDiagramInput): Promise<Diagram> {
  return apiRequest<Diagram>('/diagrams/generate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
